import { Directive, Input } from '@angular/core';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ContactService } from '../../services/contact.service';
import { GuardService } from '../../services/guard.service';
import { ICommunication } from '../../interfaces/communication/ICommunication';
import { CommunicationType } from '../../enums/CommunicationType';
import { ICommunicationTask } from '../../interfaces/communication/ICommunicationTask';
import { ICommunicationNotification } from '../../interfaces/communication/ICommunicationNotification';
import { DatePipe } from '@angular/common';
import { IS_PRODUCT_SAFETY_PROJECT, IS_TRS_PROJECT } from '../../../../current-project';

@Directive()
export class CommunicationBuilderDirective {
    subscription: Subscription[] = [];
    initials: string;
    text: string;
    private cssClass: { [key: string]: boolean } = {};
    private title: string;

    @Input() isPlaceholder: boolean;

    constructor(
        private translateService: TranslateService,
        private guardService: GuardService,
        private datePipe: DatePipe,
        private contactService: ContactService
    ) {}

    private _item: ICommunication;

    get item(): ICommunication {
        return this._item;
    }

    @Input() set item(val: ICommunication) {
        this._item = val;
        if (val) {
            this.setValues();
        }
    }

    get isTask(): boolean {
        return this.item.communicationType === CommunicationType.Task;
    }

    get task(): ICommunicationTask {
        return this.item;
    }

    get isNotification(): boolean {
        return this.item.communicationType === CommunicationType.Notification;
    }

    get notification(): ICommunicationNotification {
        return this.item;
    }

    get message(): string {
        if (this.isTask) {
            return this.task.taskType;
        }

        return this.notification.notificationType;
    }

    get forSelf(): boolean {
        return this.item.assignee && this.guardService.currentUser.id === this._item.assignee;
    }

    get bySelf(): boolean {
        return this.item.createdBy && this.guardService.currentUser.id === this.item.createdBy?.id;
    }

    async setValues() {
        const val = this._item;

        if (!val) {
            return;
        }

        this.cssClass = {
            ...this.cssClass
        };

        this.initials = '';
        this.title = '';

        if (this.isTask) {
            this.initials = this.task.orgCode;
        } else if (this.notification) {
            this.initials = this.notification.orgCode;
        }

        const contactPerson = await this.contactService.getContactPersonInfo();
        if (IS_TRS_PROJECT) {
            this.text = this.translateService.instant(
                `communication.${this.item.communicationType}.${this.message}${this.forSelf ? 'ForSelf' : ''}${this.bySelf ? 'BySelf' : ''}`,
                {
                    name: this._item?.assigneeFullName,
                    orgCode: this._item?.orgCode,
                    title: this.title,
                    by:
                        this._item?.createdBy?.id === 'Z003X1DP'
                            ? contactPerson.fullName
                            : this._item?.createdBy?.fullName || contactPerson.fullName,
                    dueDate: this.task?.dueDate ? this.datePipe.transform(new Date(this.task.dueDate)) : null,
                    ...this.item.content
                }
            );
        }

        if (IS_PRODUCT_SAFETY_PROJECT) {
            this.text = this.translateService.instant(
                `communication.${this.item.communicationType}.${this.message}${this.forSelf ? 'ForSelf' : ''}${this.bySelf ? 'BySelf' : ''}`,
                {
                    name: this._item?.assigneeFullName,
                    orgCode: this._item?.orgCode,
                    title: this.title,
                    by: this._item?.createdBy?.fullName || contactPerson.fullName,
                    dueDate: this.task?.dueDate ? this.datePipe.transform(new Date(this.task.dueDate)) : null,
                    ...this.item.content
                }
            );
        }
    }

    get isTaskFinished(): boolean {
        return (
            this.task.processSteps?.length &&
            this.task.processSteps.filter(e => e.isFinished).length === this.task.processSteps.length
        );
    }

    get isTaskUntouched(): boolean {
        return (
            this.task.processSteps?.length &&
            this.task.processSteps.filter(e => !e.isFinished).length === this.task.processSteps.length
        );
    }
}
